import React, { useState } from 'react';

import { FormattedMessage } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import Form, { Field, RequiredAsterisk } from '@atlaskit/form';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@atlaskit/modal-dialog';
import { Box, Text } from '@atlaskit/primitives';
import type { ValueType } from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';

import { usePostAdditionalFeedback } from 'src/components/code-reviewer-feedback/src/hooks/use-post-additional-feedback';
import { CodeReviewerFeedbackType } from 'src/components/code-reviewer-feedback/src/types';
import { CodeReviewerFeedbackSentiment } from 'src/components/conversation/src/internal/analytics';
import { useIntl } from 'src/hooks/intl';

import messages from '../i18n/request-additional-feedback.i18n';

import { TypeSelect } from './feedback-type-select';

interface Option {
  label: string;
  value: string;
}
type CodeReviewerExternalFeedbackModalProps = {
  onClose: () => void;
  handleFeedbackSent: () => void;
  prUrl: string;
  sentiment: CodeReviewerFeedbackSentiment;
  hasCodeSuggestions: boolean;
  codeReviewerCommentContent: any[];
};
export const AdditionalFeedbackModal = ({
  onClose,
  handleFeedbackSent,
  prUrl,
  sentiment,
  hasCodeSuggestions,
  codeReviewerCommentContent,
}: CodeReviewerExternalFeedbackModalProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const [description, setDescription] = useState<string>('');
  const [allowContact, setAllowContact] = useState<boolean>(false);
  const [{ inProgress }, { submit }] = usePostAdditionalFeedback([
    { id: 'feedback-collector-location', value: 'code-reviewer-modal' },
  ]);
  const [feedbackType, setFeedbackType] = useState<
    CodeReviewerFeedbackType | undefined
  >(undefined);

  return (
    <Modal
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEscapePress={false}
      onClose={onClose}
      testId="code-reviewer-feedback.modal"
    >
      <Form
        onSubmit={async () => {
          await submit(
            {
              feedbackType,
              feedbackDescription: description,
              allowContact,
              sentiment,
              prUrl,
              hasCodeSuggestions,
              codeReviewerCommentContent,
            },
            handleFeedbackSent
          );
          onClose();
        }}
      >
        {({ formProps }) => (
          <form {...formProps}>
            <ModalHeader>
              <ModalTitle>{formatMessage(messages.feedbackTitle)}</ModalTitle>
              <IconButton
                appearance="subtle"
                icon={iconProps => <CrossIcon {...iconProps} />}
                label={formatMessage(messages.cancelButtonLabel)}
                testId="code-reviewer.modal.cancel-button"
                onClick={onClose}
              />
            </ModalHeader>
            <ModalBody>
              <Box as="p" color="color.text.subtle" paddingBlockEnd="space.300">
                {formatMessage(messages.requiredFieldsSummary)}
                <RequiredAsterisk />
              </Box>
              <Box>
                <Field<ValueType<Option>>
                  name="type"
                  label={formatMessage(messages.feedbackTypeLabel)}
                >
                  {({ fieldProps }) => (
                    <TypeSelect {...fieldProps} onChange={setFeedbackType} />
                  )}
                </Field>
                <Field
                  label={formatMessage(messages.defaultCustomTextAreaLabel)}
                  isRequired
                  name="description"
                >
                  {({ fieldProps }) => (
                    <TextArea
                      {...fieldProps}
                      name="foo"
                      minimumRows={6}
                      onChange={e => setDescription(e.target.value)}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                      value={description}
                    />
                  )}
                </Field>
                <Field name="can-be-contacted">
                  {({ fieldProps }) => (
                    <Checkbox
                      {...fieldProps}
                      aria-describedby={undefined} // JCA11Y-1988
                      label={
                        <Text>
                          <FormattedMessage
                            {...messages.canBeContactedLabel}
                            values={{
                              privacyPolicyLink: (
                                <a
                                  href="https://www.atlassian.com/legal/privacy-policy"
                                  target="_blank"
                                >
                                  Atlassian Privacy Policy
                                </a>
                              ),
                            }}
                          />
                        </Text>
                      }
                      onChange={event => setAllowContact(event.target.checked)}
                    />
                  )}
                </Field>
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button appearance="subtle" onClick={onClose}>
                {formatMessage(messages.cancelButtonLabel)}
              </Button>
              <Button
                appearance="primary"
                type="submit"
                isDisabled={!description}
                isLoading={inProgress}
                testId="code-reviewer-feedback.modal.feedback-collector-submit-btn"
              >
                {formatMessage(messages.submitButtonLabel)}
              </Button>
            </ModalFooter>
          </form>
        )}
      </Form>
    </Modal>
  );
};
